import clsx from 'clsx';
import { ArrowRightIcon, CalendarIcon, FilmIcon } from 'lucide-preact';
import { MouseEventHandler } from 'preact/compat';

import { useViewAnnouncementMutation } from '@/hooks/announcements';
import route from '@/lib/route';
import { AnnouncementWithViewed } from '@/queries/announcements/types';

import { SpeakerIcon } from '../icons';

interface AnnouncementItemProps {
  item: AnnouncementWithViewed;
}

const AnnouncementItem = ({ item }: AnnouncementItemProps) => {
  const isSameOrigin =
    item.actionURL.startsWith('/') || (typeof window !== 'undefined' && item.actionURL.startsWith(window.location.origin));

  const isAnimalAlert = item.type === 'animal-alert';

  const Icon = item.type === 'video' ? FilmIcon : item.type === 'event' ? CalendarIcon : SpeakerIcon;

  const { mutate } = useViewAnnouncementMutation();

  const onClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();

    mutate(item._id);
    route(item.actionURL, false, true);
  };

  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    <a
      data-native
      className={clsx(
        'preflight preflight-a block rounded-lg p-0.5',
        isAnimalAlert ? 'bg-[linear-gradient(to_right,#CDD565,#FFCE1F,#F18C43,#F9A5E7)]' : 'bg-primary-green',
      )}
      href={item.actionURL}
      target={isSameOrigin ? undefined : '_blank'}
      rel={isSameOrigin ? undefined : 'noopener noreferrer'}
      onClick={onClick}
    >
      <div className={clsx('flex rounded-md px-2 py-3', isAnimalAlert ? 'bg-sun-50' : 'bg-white hover:bg-beige-light')}>
        <div className="preflight relative mr-2">
          <div
            className={clsx(
              'preflight flex size-10 items-center justify-center rounded-full',
              isAnimalAlert ? 'bg-[linear-gradient(to_bottom_right,#CDD565,#FFCE1F,#F18C43,#F9A5E7)]' : 'bg-primary-green',
            )}
          >
            <span className="sr-only">{item.type}</span>
            <Icon className="size-5 text-white" />
          </div>

          {!item.isViewed && <i className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-blue-1" />}
        </div>

        <div className="grow">
          <h4 className="preflight preflight-h4 text-pretty text-sm font-semibold">{item.title}</h4>
          <p className="preflight preflight-p mt-1 flex items-center text-pretty text-xs text-grey-6">
            {isAnimalAlert ? (
              <>
                <span className="font-semibold text-stone-900 underline">{item.body}</span>
                <ArrowRightIcon className="ml-2 inline-block size-4 text-stone-900" />
              </>
            ) : (
              item.body
            )}
          </p>
        </div>
      </div>
    </a>
  );
};

export default AnnouncementItem;
